import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import useTracking from '@gameday/utils/hooks/useTracking';
import {
    TopPerformersWrapper,
    TitleWrapper,
    PlayerListWrapper
} from './TopPerformers.style';
import { withTheme } from 'styled-components';
import Skeleton from '@mlb/react-patterns/v4/components/Skeleton/skeleton';
import { useTranslate } from '@gameday/utils/hooks/useTranslate';
import ControlledOverflow from '@mlb/react-patterns/v4/components/ControlledOverflow';
import TopPerformersPlayer, { PlayerSkeleton } from './TopPerformersPlayer';

const TopPerformers = ({
    players = [],
    isLoading,
    numPlayerSkeletons = 2,
    deviceInteraction = {},
    sportId,
    language = 'en',
    isMobile = false,
    viewType = '',
    headshotSize = 64,
    showPlayerStories = false,
    isLoadingStories = false,
    isLoadingTopPerformers = false,
    condensedStatLine = false
}) => {
    const Tracking = useTracking();
    const translator = useTranslate();

    const playersArr = useMemo(() => {
        if (isLoading) {
            return new Array(numPlayerSkeletons).fill(0).map((id, i) => {
                return <PlayerSkeleton key={i} iteration={i} />;
            });
        }

        return players.map((player, i) => {
            const showStory = showPlayerStories && player.hasStory;
            if (isLoadingTopPerformers) {
                return <PlayerSkeleton key={i} iteration={i} />;
            }
            return <TopPerformersPlayer
                key={i}
                player={player}
                iteration={i}
                isLoading={false}
                language={language}
                deviceInteraction={deviceInteraction}
                sportId={sportId}
                tracking={Tracking}
                isMobile={isMobile}
                headshotSize={headshotSize + 'px'}
                showStory={showStory}
                isLoadingStories={isLoadingStories}
                isLoadingTopPerformers={isLoadingTopPerformers}
                condensedStatLine={condensedStatLine}
            />;
        });
    }, [players, isLoading, language, isMobile, headshotSize, condensedStatLine, isLoadingTopPerformers, isLoadingStories, deviceInteraction]);

    const title = translator('label_top_performers', 'Top Performers');
    const showCarousel = (viewType !== 'mini' && isMobile);

    return (
        <TopPerformersWrapper
            showCarousel={ showCarousel }
            $viewType={viewType}
        >
            <TitleWrapper
                showCarousel={ showCarousel }
                $viewType={viewType}
            >
                { isLoading
                    ? <Skeleton height='17px' width='129px' />
                    : title
                }
            </TitleWrapper>
            { !isMobile
                ? <PlayerListWrapper>
                    {playersArr}
                </PlayerListWrapper>
                : <ControlledOverflow
                    disableShadows
                    addEndInsert
                    endInsertWidth={ 8 }
                >
                    { playersArr }
                </ControlledOverflow> }
        </TopPerformersWrapper>
    );
};

TopPerformers.propTypes = {
    players: PropTypes.arrayOf(PropTypes.object),
    isLoading: PropTypes.bool,
    numPlayerSkeletons: PropTypes.number,
    deviceInteraction: PropTypes.object,
    language: PropTypes.string,
    sportId: PropTypes.number,
    isMobile: PropTypes.bool,
    headshotSize: PropTypes.string,
    showPlayerStories: PropTypes.bool,
    isLoadingStories: PropTypes.bool,
    isLoadingTopPerformers: PropTypes.bool,
    condensedStatLine: PropTypes.bool
};

export default withTheme(TopPerformers);
